import type { PropsWithChildren, ReactElement } from 'react';
import { useEffect } from 'react';
import { Route, Switch } from 'react-router-dom';

import { LoginCallback, SecureRoute, useOktaAuth } from '@okta/okta-react';
import { StateInspector } from 'reinspect';

import { SplashLoader } from '../../components';
import { environment } from '../../environment';
import FaqPage from '../../features/FaqPage';
import HelpPage from '../../features/HelpPage';
import NotFoundPage from '../../features/NotFoundPage';
import ProtectedRoute from '../../features/ProtectedRoute';
import TrackerPage from '../../features/TrackerPage';
import { reducer, storeInitialState, StoreProvider } from '../../store';

const StateInspectorWrapper = StateInspector as unknown as (
  props: PropsWithChildren<{ name: string }>
) => ReactElement;

function RootRoutes() {
  const { authState } = useOktaAuth();

  useEffect(() => {
    const script = document.createElement('script');
    const env = import.meta.env.VITE_env;

    if (env === 'dev') {
      // script.src = '//assets.adobedtm.com/47e3e446470b/cb8044b4055e/launch-9a14eb8d1967-development.min.js';
    } else if (env === 'stage') {
      script.src = '//assets.adobedtm.com/47e3e446470b/cb8044b4055e/launch-e45ce7add0e8-staging.min.js';
    } else if (env === 'prod') {
      script.src = '//assets.adobedtm.com/47e3e446470b/cb8044b4055e/launch-ef46b7d8dae8.min.js';
    } else {
      // eslint-disable-next-line no-console
      console.log('No Adobe Analytics script injected for this environment: ' + env);
    }

    document.body.appendChild(script);

    return () => {
      document.body.removeChild(script);
    };
  }, []);

  if (!authState || authState.isPending) {
    return <SplashLoader />;
  }

  const setEnvironment = environment().env;
  const shouldShowStateInspector =
    !setEnvironment || setEnvironment === 'dev' || setEnvironment === 'local';

  const routes = (
    <Switch>
      <SecureRoute path="/secure" component={ProtectedRoute} />
      <SecureRoute exact path="/help" component={HelpPage} />
      <SecureRoute exact path="/tracker" component={TrackerPage} />
      <Route exact path="/FaqPage" component={FaqPage} />
      <Route exact path="/login/callback" component={LoginCallback} />
      <Route component={NotFoundPage} />
    </Switch>
  );

  const content = (
    <StoreProvider reducer={reducer} initialState={storeInitialState}>
      {routes}
    </StoreProvider>
  );

  if (shouldShowStateInspector) {
    return <StateInspectorWrapper name="redCarpet">{content}</StateInspectorWrapper>;
  }

  return content;
}

export default RootRoutes;